import { rad2deg } from "../math/angles";
import type { Seismic } from "../types/input";
import type { SeismicResult } from "../types/result";
import type { WarningCode } from "../warnings";
import { siteFactorF1, siteFactorFS } from "./tables";

/** θ = atan(m·kh / kvFactor); kvFactor = 1 ∓ kv (Denk. 16.26a); m su düzeltme çarpanı (16.26b/c). */
export const thetaFor = (kh: number, kvFactor: number, multiplier = 1): number => Math.atan((multiplier * kh) / kvFactor);

/** Tablo 16.7: r=2 → 120·SDS mm, r=1.5 → 80·SDS mm, r=1 → izin yok. */
export function allowedDisplacementMm(r: number, SDS: number): number | null {
  if (r === 2) return 120 * SDS;
  if (r === 1.5) return 80 * SDS;
  return null;
}

export function computeSeismic(s: Seismic, warn: (code: WarningCode) => void): SeismicResult {
  if (s.inputMode === "ec8") {
    if (!s.ec8) throw new Error("EC8 modunda ag/g ve S zorunludur");
    const alpha = s.ec8.agOverG * s.ec8.gammaI;
    const r = s.r;
    const kh = (alpha * s.ec8.S) / r;
    const kv = s.ec8.avgOverAg > 0.6 ? 0.5 * kh : 0.33 * kh; // EN 1998-5 Denk. 7.2
    const allowed = r === 2 ? 300 * alpha * s.ec8.S : r === 1.5 ? 200 * alpha * s.ec8.S : null; // Tablo 7.1 (mm)
    return { source: "ec8", r, kh, kv, khWater: kh, allowedDisplacementMm: allowed, thetaUpDeg: rad2deg(thetaFor(kh, 1 - kv)), thetaDownDeg: rad2deg(thetaFor(kh, 1 + kv)) };
  }
  if (s.inputMode === "aashto") {
    if (!s.aashto) throw new Error("AASHTO modunda PGA zorunludur");
    const kmax = s.aashto.fpga * s.aashto.pga;
    const kh = s.aashto.displacementAllowed ? 0.5 * kmax : kmax; // AASHTO LRFD 11.6.5.2.2 (1–2 inç yer değiştirme)
    return { source: "aashto", r: s.aashto.displacementAllowed ? 2 : 1, kh, kv: 0, khWater: kh, allowedDisplacementMm: s.aashto.displacementAllowed ? 50 : null, thetaUpDeg: rad2deg(thetaFor(kh, 1)), thetaDownDeg: rad2deg(thetaFor(kh, 1)) };
  }
  if (s.inputMode === "coefficients") {
    if (s.kh === undefined) throw new Error("Katsayı modunda kh zorunludur");
    const kh = s.kh, kv = s.kv ?? 0;
    warn("SEISMIC_DIRECT_COEFF");
    return {
      source: "direct", r: s.r, kh, kv, khWater: kh, allowedDisplacementMm: null,
      thetaUpDeg: rad2deg(thetaFor(kh, 1 - kv)), thetaDownDeg: rad2deg(thetaFor(kh, 1 + kv)),
    };
  }
  let SDS: number, FS: number | undefined, F1: number | undefined, SD1: number | undefined;
  if (s.inputMode === "map") {
    if (s.SS === undefined || s.siteClass === undefined) throw new Error("Harita modunda SS ve zemin sınıfı zorunludur");
    FS = siteFactorFS(s.siteClass, s.SS);
    SDS = s.SS * FS;
    if (s.S1 !== undefined) { F1 = siteFactorF1(s.siteClass, s.S1); SD1 = s.S1 * F1; }
  } else {
    if (s.SDS === undefined) throw new Error("Doğrudan modda SDS zorunludur");
    SDS = s.SDS;
  }
  let r: number = s.r;
  if (s.saturatedSoilLimitsR && r > 1) { r = 1; warn("R_SATURATED"); }
  const kh = (0.4 * SDS) / r;
  const kv = 0.5 * kh;
  return {
    source: "tbdy", SS: s.SS, S1: s.S1, FS, F1, SDS, SD1, r, kh, kv, khWater: 0.4 * SDS,
    allowedDisplacementMm: allowedDisplacementMm(r, SDS),
    thetaUpDeg: rad2deg(thetaFor(kh, 1 - kv)),
    thetaDownDeg: rad2deg(thetaFor(kh, 1 + kv)),
  };
}
